import { useCallback, useEffect, useMemo, useSyncExternalStore } from 'react';

import type { OrgSummary } from './api.ts';

/**
 * Active-org state lives in localStorage and is reactive via
 * `useSyncExternalStore`, so the org switcher in nav stays in sync
 * across tabs / components without a context provider.
 */

const KEY = 'ta_active_org';
// The sign-in org that last drove the active-org value. Lets us tell a *fresh*
// sign-in pick (honor it, even over a stale stored value) apart from a
// mid-session reload (keep whatever the user switched to in-app). See
// planActiveOrgOnSession.
const LOGIN_KEY = 'ta_active_org_login';

export function getStoredActiveOrgSlug(): string | null {
  try {
    return localStorage.getItem(KEY);
  } catch {
    return null;
  }
}

export function setStoredActiveOrgSlug(slug: string): void {
  try {
    localStorage.setItem(KEY, slug);
    // Same-tab listeners (storage events don't fire in the writing tab).
    window.dispatchEvent(new Event('ta:active-org'));
  } catch {
    // ignore (private mode, etc.)
  }
}

function getStoredLoginOrgSlug(): string | null {
  try {
    return localStorage.getItem(LOGIN_KEY);
  } catch {
    return null;
  }
}

// Records the sign-in org behind the current active value. Deliberately does
// NOT dispatch the active-org event — it's bookkeeping, not a visible change.
function setStoredLoginOrgSlug(slug: string): void {
  try {
    localStorage.setItem(LOGIN_KEY, slug);
  } catch {
    // ignore (private mode, etc.)
  }
}

/**
 * Forget the stored active org (and its sign-in provenance). Called on
 * sign-out so the next sign-in's org pick starts from a clean slate.
 */
export function clearStoredActiveOrgSlug(): void {
  try {
    localStorage.removeItem(KEY);
    localStorage.removeItem(LOGIN_KEY);
    window.dispatchEvent(new Event('ta:active-org'));
  } catch {
    // ignore (private mode, etc.)
  }
}

function subscribe(listener: () => void): () => void {
  const onStorage = (e: StorageEvent) => {
    if (e.key === KEY) listener();
  };
  window.addEventListener('storage', onStorage);
  window.addEventListener('ta:active-org', listener);
  return () => {
    window.removeEventListener('storage', onStorage);
    window.removeEventListener('ta:active-org', listener);
  };
}

function getSnapshot(): string | null {
  return getStoredActiveOrgSlug();
}

function getServerSnapshot(): string | null {
  return null;
}

/**
 * Resolve the active org from a list of memberships. Prefers the value
 * stashed in localStorage; falls back to the oldest (first) org. Returns
 * null if the user has no orgs.
 */
export function resolveActiveOrg(orgs: OrgSummary[], stored: string | null): OrgSummary | null {
  if (orgs.length === 0) return null;
  if (stored) {
    const match = orgs.find((o) => o.slug === stored);
    if (match) return match;
  }
  return orgs[0]!;
}

export function useActiveOrg(orgs: OrgSummary[]) {
  const stored = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  const active = useMemo(() => resolveActiveOrg(orgs, stored), [orgs, stored]);
  const switchActive = useCallback((slug: string) => {
    setStoredActiveOrgSlug(slug);
  }, []);
  return { active, switchActive };
}

/**
 * Decide what to write to the active-org store given the org the user picked
 * at sign-in (`selectedSlug`, from /me) and the state we already hold. The
 * key insight: `selectedSlug` is fixed for a whole session, so we can't just
 * always prefer it (that would fight in-app switching on every reload) nor
 * only fill an empty slot (that lets a *stale* stored value from a previous
 * session shadow a fresh pick — the reported bug). We disambiguate with
 * `lastLoginOrg`, the sign-in org behind the current stored value:
 *
 *   - fresh sign-in (`selectedSlug` !== `lastLoginOrg`) → adopt the pick,
 *     overriding any stale stored value;
 *   - same session, nothing stored yet → adopt the pick (initial seed);
 *   - same session with a stored value → keep it (respects an in-app switch).
 *
 * Returns the writes to apply (either field omitted = leave as-is). Pure so
 * the precedence is unit-tested.
 */
export function planActiveOrgOnSession(args: {
  stored: string | null;
  lastLoginOrg: string | null;
  selectedSlug: string | null;
  orgs: OrgSummary[];
}): { activeOrg?: string; loginOrg?: string } {
  if (!args.selectedSlug) return {}; // no sign-in selection to honor
  if (!args.orgs.some((o) => o.slug === args.selectedSlug)) return {}; // pick isn't a membership
  const isFreshLogin = args.lastLoginOrg !== args.selectedSlug;
  const out: { activeOrg?: string; loginOrg?: string } = { loginOrg: args.selectedSlug };
  if (isFreshLogin || !args.stored) out.activeOrg = args.selectedSlug;
  return out;
}

/**
 * Make the WorkOS org picker actually stick. On a fresh sign-in the picked org
 * becomes the active org (even over a stale stored value from a previous
 * session); within a session, in-app switches still win and persist across
 * reloads. Every `useActiveOrg` consumer keeps reading the single localStorage
 * seam.
 */
export function useSeedActiveOrgFromSession(
  selectedSlug: string | null,
  orgs: OrgSummary[],
): void {
  const stored = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  useEffect(() => {
    const plan = planActiveOrgOnSession({
      stored,
      lastLoginOrg: getStoredLoginOrgSlug(),
      selectedSlug,
      orgs,
    });
    if (plan.loginOrg !== undefined) setStoredLoginOrgSlug(plan.loginOrg);
    if (plan.activeOrg !== undefined) setStoredActiveOrgSlug(plan.activeOrg);
  }, [stored, selectedSlug, orgs]);
}
